import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Button,
  Grid,
  Skeleton,
  CssBaseline,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import SearchIcon from "@mui/icons-material/Search";
import EventIcon from "@mui/icons-material/Event";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { CSVLink } from "react-csv";
import moment from "moment";
import { showError } from "../../../toastify";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useRequiredAuth } from "../../common/guard/requiredAuth";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  ChartTooltip,
  Legend,
);

const T = {
  pageBg: "#eceef2",
  cardBg: "#ffffff",
  cardRadius: 12,
  cardShadow: "0 1px 3px rgba(15,23,42,0.08)",
  cardBorder: "1px solid #e2e8f0",
  primary: "#1e293b",
  muted: "#64748b",
  blue: "#1565c0",
  bluePale: "#e3f2fd",
  green: "#2e7d32",
  greenPale: "#e8f5e9",
  purple: "#6a1b9a",
  purplePale: "#f3e5f5",
  red: "#c62828",
  redPale: "#ffebee",
  chartBlue: "#2196f3",
  chartGreen: "#4caf50",
  chartOrange: "#ff9800",
  chartPurple: "#9c27b0",
};

const DEFAULT_PAGE_SIZE = 25;

const pct = (num, denom) => {
  if (!denom) return "0%";
  return ((num / denom) * 100).toFixed(1) + "%";
};

const fmt = (n) => Number(n || 0).toLocaleString();

// ── Sub-components ──────────────────────────────────────────────────────────

function ViewLevelBanner() {
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: T.cardBg,
        border: T.cardBorder,
        borderRadius: `${T.cardRadius}px`,
        boxShadow: T.cardShadow,
        mb: 2,
        px: 3,
        py: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <InfoOutlinedIcon sx={{ color: T.blue, mt: "2px", fontSize: 20 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", color: T.primary, mb: 0.25 }}>
              View level access
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: T.blue }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: T.blue }}>National / REB</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.6875rem", color: T.muted }}>national dashboard</Typography>
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem />
        {[
          { label: "DDE", desc: "one per district, sees assigned district only" },
          { label: "DEO", desc: "district-level officer, sees assigned district only based on login credential" },
          { label: "Head Teacher", desc: "sees assigned school only" },
          { label: "Teacher", desc: "sees personal CPD progress only" },
        ].map((item) => (
          <Box key={item.label}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: T.blue }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: T.blue }}>{item.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: "0.6875rem", color: T.muted, maxWidth: 160 }}>{item.desc}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

function KpiCard({ icon, iconBg, iconColor, label, value, sub, subColor }) {
  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: T.cardBg,
        border: T.cardBorder,
        borderRadius: `${T.cardRadius}px`,
        boxShadow: T.cardShadow,
        height: "100%",
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, px: 2.5, py: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 26 } })}
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.75rem", color: T.muted, mb: 0.25 }}>{label}</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: T.primary, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: "0.75rem", color: subColor || T.muted, fontWeight: 600, mt: 0.25 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function EmptyChart({ message }) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed #cbd5e1",
        borderRadius: 1,
        color: T.muted,
        fontSize: "0.8125rem",
        px: 2,
        textAlign: "center",
      }}
    >
      {message}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const TrainingAttendanceReport = ({ user }) => {
  const { hasPermissions } = useRequiredAuth();

  useEffect(() => {
    document.title = "TMIS | CPD — Attendance report";
  }, []);

  const [fromDate, setFromDate] = useState(
    () => moment().subtract(11, "months").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(() => moment().format("YYYY-MM-DD"));
  const [searchDraft, setSearchDraft] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [trainingPick, setTrainingPick] = useState(null);
  const [trainingChoices, setTrainingChoices] = useState([]);

  const [districts, setDistricts] = useState([]);
  const [districtPick, setDistrictPick] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [sectorPick, setSectorPick] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providerPick, setProviderPick] = useState(null);
  const [educationLevels, setEducationLevels] = useState([]);
  const [educationLevelPick, setEducationLevelPick] = useState(null);
  const [positions, setPositions] = useState([]);
  const [positionPick, setPositionPick] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState({
    trend: [],
    attendanceSplit: { present: 0, absent: 0, excused: 0 },
    topTrainings: [],
    byDistrict: [],
    uniqueTeachers: 0,
    certified: 0,
    byPosition: [],
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(
          "/api/cpd/trainings?" +
            new URLSearchParams({ page: "1", size: "300", searchTerm: "" }),
        );
        setTrainingChoices(
          Array.isArray(data.records) ? data.records.filter((t) => t?.id) : [],
        );
      } catch (_) {
        setTrainingChoices([]);
      }
    };
    load();
  }, [user?.selectedEntity]);

  // Reference lookups for the filter row — loaded once, independent of the report's own filters.
  useEffect(() => {
    const load = async () => {
      try {
        const [distRes, provRes, levelRes, posRes] = await Promise.all([
          axios.get("/api/posts/districts"),
          axios.get(
            "/api/cpd/training-providers?" +
              new URLSearchParams({ page: "1", size: "300", searchTerm: "" }),
          ),
          axios.get("/api/posts/school-levels"),
          axios.get("/api/posts/dropdown-positions").catch(() => ({ data: [] })),
        ]);
        setDistricts(Array.isArray(distRes.data) ? distRes.data : []);
        setProviders(
          Array.isArray(provRes.data?.records) ? provRes.data.records : [],
        );
        setEducationLevels(Array.isArray(levelRes.data) ? levelRes.data : []);
        // GetDropdownPositions can list the same position under multiple teaching levels/scopes.
        const rawPositions = Array.isArray(posRes.data) ? posRes.data : [];
        const seenPositionIds = new Set();
        setPositions(
          rawPositions.filter((p) => {
            if (!p?.id || seenPositionIds.has(p.id)) return false;
            seenPositionIds.add(p.id);
            return true;
          }),
        );
      } catch (_) {
        setDistricts([]);
        setProviders([]);
        setEducationLevels([]);
        setPositions([]);
      }
    };
    load();
  }, []);

  // Sectors cascade off the selected district.
  useEffect(() => {
    if (!districtPick?.id) {
      setSectors([]);
      return;
    }
    const load = async () => {
      try {
        const { data } = await axios.get(
          `/api/posts/sectors/${districtPick.id}`,
        );
        setSectors(Array.isArray(data) ? data : []);
      } catch (_) {
        setSectors([]);
      }
    };
    load();
  }, [districtPick?.id]);

  const buildFilterQuery = useCallback(() => {
    const base = new URLSearchParams({ from: fromDate, to: toDate });
    if (appliedSearch.trim()) base.append("searchTerm", appliedSearch.trim());
    if (trainingPick?.id) base.append("trainingId", String(trainingPick.id));
    if (districtPick?.id) base.append("districtId", String(districtPick.id));
    if (sectorPick?.id) base.append("sectorId", String(sectorPick.id));
    if (providerPick?.id) base.append("providerId", String(providerPick.id));
    if (positionPick?.id) base.append("positionId", String(positionPick.id));
    if (educationLevelPick?.id)
      base.append("schoolLevelId", String(educationLevelPick.id));
    return base;
  }, [
    fromDate,
    toDate,
    appliedSearch,
    trainingPick?.id,
    districtPick?.id,
    sectorPick?.id,
    providerPick?.id,
    positionPick?.id,
    educationLevelPick?.id,
  ]);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setSummaryLoading(true);
    try {
      const listQs = buildFilterQuery();
      listQs.set("page", String(page));
      listQs.set("size", String(pageSize));
      const sumQs = buildFilterQuery();

      const [listRes, sumRes] = await Promise.all([
        axios.get(`/api/cpd/training-attendance-report?${listQs}`),
        axios.get(`/api/cpd/training-attendance-report/summary?${sumQs}`),
      ]);

      const d = listRes.data;
      setRows(Array.isArray(d.records) ? d.records : []);
      setTotalRecords(d.totalRecords || 0);

      setSummary({
        trend: Array.isArray(sumRes.data?.trend) ? sumRes.data.trend : [],
        attendanceSplit: {
          present: Number(sumRes.data?.attendanceSplit?.present ?? 0) || 0,
          absent: Number(sumRes.data?.attendanceSplit?.absent ?? 0) || 0,
          excused: Number(sumRes.data?.attendanceSplit?.excused ?? 0) || 0,
        },
        topTrainings: Array.isArray(sumRes.data?.topTrainings)
          ? sumRes.data.topTrainings
          : [],
        byDistrict: Array.isArray(sumRes.data?.byDistrict)
          ? sumRes.data.byDistrict
          : [],
        uniqueTeachers: Number(sumRes.data?.uniqueTeachers ?? 0) || 0,
        certified: Number(sumRes.data?.certified ?? 0) || 0,
        byPosition: Array.isArray(sumRes.data?.byPosition)
          ? sumRes.data.byPosition
          : [],
      });
    } catch (e) {
      showError(e);
      setRows([]);
      setTotalRecords(0);
      setSummary({
        trend: [],
        attendanceSplit: { present: 0, absent: 0, excused: 0 },
        topTrainings: [],
        byDistrict: [],
        uniqueTeachers: 0,
        certified: 0,
        byPosition: [],
      });
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, [page, pageSize, buildFilterQuery]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleReset = () => {
    setFromDate(moment().subtract(11, "months").format("YYYY-MM-DD"));
    setToDate(moment().format("YYYY-MM-DD"));
    setSearchDraft("");
    setAppliedSearch("");
    setTrainingPick(null);
    setDistrictPick(null);
    setSectorPick(null);
    setProviderPick(null);
    setEducationLevelPick(null);
    setPositionPick(null);
    setPage(1);
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const { present, absent, excused } = summary.attendanceSplit;
  const { uniqueTeachers, certified } = summary;
  const expectedRecords = totalRecords;
  const notAttended = absent;
  const base = expectedRecords || 1;
  const certBase = uniqueTeachers || 1;

  // ── Chart data ─────────────────────────────────────────────────────────────

  const attendanceDonutData = useMemo(
    () => ({
      labels: ["Attended", "Absent", "Excused"],
      datasets: [
        {
          data: [present, absent, excused],
          backgroundColor: [T.chartBlue, T.chartGreen, T.chartOrange],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [present, absent, excused],
  );

  const certificationDonutData = useMemo(() => {
    const notCertified = Math.max(0, uniqueTeachers - certified);
    return {
      labels: ["Certified", "Not Certified"],
      datasets: [
        {
          data: [certified, notCertified],
          backgroundColor: [T.chartBlue, T.chartGreen],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    };
  }, [certified, uniqueTeachers]);

  const donutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: {
          position: "right",
          labels: { boxWidth: 12, font: { size: 11 }, padding: 10, color: T.primary },
        },
        tooltip: {
          backgroundColor: "rgba(30,41,59,0.92)",
          padding: 10,
          cornerRadius: 8,
        },
      },
    }),
    [],
  );

  const districtBarData = useMemo(() => {
    const items = summary.byDistrict || [];
    return {
      labels: items.map((d) => d.districtName || "Unknown"),
      datasets: [
        {
          label: "Sessions",
          data: items.map((d) => d.sessionCount ?? 0),
          backgroundColor: T.chartBlue,
          borderRadius: 3,
          barThickness: 18,
        },
      ],
    };
  }, [summary.byDistrict]);

  const districtBarOpts = useMemo(() => {
    const peak = Math.max(...(summary.byDistrict || []).map((d) => Number(d.sessionCount ?? 0)), 1);
    return {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: "rgba(30,41,59,0.92)", padding: 10, cornerRadius: 8 },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: Math.ceil(peak / 500) * 500 || 500,
          ticks: { precision: 0, color: T.muted, font: { size: 10 } },
          grid: { color: "rgba(148,163,184,0.35)" },
          border: { display: false },
        },
        y: {
          ticks: { color: T.primary, font: { size: 11 } },
          grid: { display: false },
          border: { display: false },
        },
      },
    };
  }, [summary.byDistrict]);

  const topTrainingsBarData = useMemo(() => {
    const items = summary.topTrainings || [];
    return {
      labels: items.map(
        (t) =>
          (t.trainingTitle || "Training").substring(0, 42) +
          ((t.trainingTitle || "").length > 42 ? "…" : ""),
      ),
      datasets: [
        {
          label: "Sessions",
          data: items.map((t) => t.sessionCount ?? 0),
          backgroundColor: T.chartBlue,
          borderRadius: 3,
          barThickness: 18,
        },
      ],
    };
  }, [summary.topTrainings]);

  const topTrainingsBarOpts = useMemo(() => {
    const peak = Math.max(...(summary.topTrainings || []).map((t) => Number(t.sessionCount ?? 0)), 1);
    return {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: "rgba(30,41,59,0.92)", padding: 10, cornerRadius: 8 },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: Math.ceil(peak / 500) * 500 || 500,
          ticks: { precision: 0, color: T.muted, font: { size: 10 } },
          grid: { color: "rgba(148,163,184,0.35)" },
          border: { display: false },
        },
        y: {
          ticks: { color: T.primary, font: { size: 11 } },
          grid: { display: false },
          border: { display: false },
        },
      },
    };
  }, [summary.topTrainings]);

  const positionBarData = useMemo(() => {
    const items = summary.byPosition || [];
    return {
      labels: items.map((p) => p.positionName || "Unknown"),
      datasets: [
        {
          label: "Teachers",
          data: items.map((p) => p.teacherCount ?? 0),
          backgroundColor: T.chartBlue,
          borderRadius: 3,
          barThickness: 18,
        },
      ],
    };
  }, [summary.byPosition]);

  const positionBarOpts = useMemo(() => {
    const peak = Math.max(...(summary.byPosition || []).map((p) => Number(p.teacherCount ?? 0)), 1);
    return {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: { backgroundColor: "rgba(30,41,59,0.92)", padding: 10, cornerRadius: 8 },
      },
      scales: {
        x: {
          beginAtZero: true,
          max: Math.ceil(peak / 500) * 500 || 500,
          ticks: { precision: 0, color: T.muted, font: { size: 10 } },
          grid: { color: "rgba(148,163,184,0.35)" },
          border: { display: false },
        },
        y: {
          ticks: { color: T.primary, font: { size: 11 } },
          grid: { display: false },
          border: { display: false },
        },
      },
    };
  }, [summary.byPosition]);

  // ── Pagination ─────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  useEffect(() => {
    if (page > totalPages && totalRecords >= 0) setPage(totalPages);
  }, [page, totalPages, totalRecords]);

  // ── CSV export ─────────────────────────────────────────────────────────────

  const csvRows = useMemo(
    () =>
      rows.map((r) => ({
        trainingId: r.trainingId,
        trainingTitle: r.trainingTitle || "",
        program: r.programName || "",
        subProgram: r.subProgramName || "",
        providerName: r.providerName || "",
        traineeName: r.traineeName || "",
        employeeId: r.employeeId || "",
        gender: r.gender || "",
        dateOfBirth: r.dateOfBirth ? moment(r.dateOfBirth).format("YYYY-MM-DD") : "",
        qualification: r.qualification || "",
        specialization: r.specialization || "",
        sessionDate: r.attendanceDate ? moment(r.attendanceDate).format("YYYY-MM-DD") : "",
        cohortStart: r.cohortStartDate ? moment(r.cohortStartDate).format("YYYY-MM-DD") : "",
        cohortEnd: r.cohortEndDate ? moment(r.cohortEndDate).format("YYYY-MM-DD") : "",
        venueName: r.venueName || "",
        sector: r.sectorName || "",
        district: r.districtName || "",
        attended: formatMark(r.attendanceMark),
        detail: formatRemark(r.attendanceRemark, r.attendanceMark),
        topicsTrainedOn: r.topicsCovered || "",
      })),
    [rows],
  );

  // ── Guard ──────────────────────────────────────────────────────────────────

  if (!hasPermissions(["CPD_TRAININGS_ATTENDANCE"])) {
    return <Redirect to="/in/cpd/dashboard" />;
  }

  const cardShell = {
    bgcolor: T.cardBg,
    borderRadius: `${T.cardRadius}px`,
    boxShadow: T.cardShadow,
    border: T.cardBorder,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: "100%", bgcolor: T.pageBg, py: 3, px: { xs: 2, sm: 3 } }}>
      <CssBaseline />

      <Box sx={{ maxWidth: 1280, mx: "auto" }}>

        {/* View level access banner */}
        <ViewLevelBanner />

        {/* ── Filter row ───────────────────────────────────────────────────── */}
        <Card elevation={0} sx={{ ...cardShell, mb: 2, px: 2.5, py: 2 }}>
          <Grid container spacing={1.5} alignItems="flex-end">
            <Grid item xs={6} sm={4} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>District</InputLabel>
                <Select
                  value={districtPick?.id || ""}
                  label="District"
                  onChange={(e) => {
                    const id = e.target.value;
                    setDistrictPick(districts.find((d) => d.id === id) || null);
                    setSectorPick(null);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {districts.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              <FormControl size="small" fullWidth disabled={!districtPick?.id}>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={sectorPick?.id || ""}
                  label="Sector"
                  onChange={(e) => {
                    const id = e.target.value;
                    setSectorPick(sectors.find((s) => s.id === id) || null);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All Sectors</MenuItem>
                  {sectors.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={4} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <CSVLink
                data={csvRows}
                filename={`cpd-report_${moment().format("YYYY-MM-DD_HHmm")}.csv`}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={!csvRows.length}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Export
                </Button>
              </CSVLink>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshOutlinedIcon />}
                onClick={handleReset}
                sx={{ textTransform: "none" }}
              >
                Reset
              </Button>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={6} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={providerPick?.id || ""}
                  label="Provider"
                  onChange={(e) => {
                    const id = e.target.value;
                    setProviderPick(providers.find((p) => p.id === id) || null);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Autocomplete
                size="small"
                options={trainingChoices}
                getOptionLabel={(o) => `${o.title || "(No title)"} — ${o.id || ""}`}
                value={trainingPick}
                onChange={(_, v) => { setTrainingPick(v || null); setPage(1); }}
                renderInput={(params) => (
                  <TextField {...params} label="Trainings" variant="outlined" placeholder="All Trainings" />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Education Level</InputLabel>
                <Select
                  value={educationLevelPick?.id || ""}
                  label="Education Level"
                  onChange={(e) => {
                    const id = e.target.value;
                    setEducationLevelPick(
                      educationLevels.find((l) => l.id === id) || null,
                    );
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  {educationLevels.map((l) => (
                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={positionPick?.id || ""}
                  label="Position"
                  onChange={(e) => {
                    const id = e.target.value;
                    setPositionPick(positions.find((p) => p.id === id) || null);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All Positions</MenuItem>
                  {positions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Sessions from"
                type="date"
                value={fromDate}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Sessions to"
                type="date"
                value={toDate}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              />
            </Grid>
          </Grid>

          {/* Search bar */}
          <Box sx={{ mt: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              sx={{ flex: 1 }}
              placeholder="Search teacher, training..."
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyPress={(ev) => {
                if (ev.key === "Enter") { setAppliedSearch(searchDraft.trim()); setPage(1); }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: T.muted, fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              disableElevation
              size="small"
              sx={{ bgcolor: T.blue, textTransform: "none", fontWeight: 600, px: 2.5 }}
              onClick={() => { setAppliedSearch(searchDraft.trim()); setPage(1); }}
            >
              Search
            </Button>
          </Box>
        </Card>

        {/* ── KPI Cards ────────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={12 / 5}>
            {summaryLoading ? (
              <Skeleton variant="rounded" height={96} />
            ) : (
              <KpiCard
                icon={<PeopleOutlinedIcon />}
                iconBg={T.bluePale}
                iconColor={T.blue}
                label="Expected Records"
                value={fmt(expectedRecords)}
                sub="Targeted records for period"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={12 / 5}>
            {summaryLoading ? (
              <Skeleton variant="rounded" height={96} />
            ) : (
              <KpiCard
                icon={<HowToRegOutlinedIcon />}
                iconBg={T.greenPale}
                iconColor={T.green}
                label="Unique Teachers"
                value={fmt(uniqueTeachers)}
                sub="Teachers in target population"
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={12 / 5}>
            {summaryLoading ? (
              <Skeleton variant="rounded" height={96} />
            ) : (
              <KpiCard
                icon={<CheckCircleOutlinedIcon />}
                iconBg={T.bluePale}
                iconColor={T.blue}
                label="Attended"
                value={fmt(present)}
                sub={`${pct(present, base)} vs expected`}
                subColor={T.green}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={12 / 5}>
            {summaryLoading ? (
              <Skeleton variant="rounded" height={96} />
            ) : (
              <KpiCard
                icon={<WorkspacePremiumOutlinedIcon />}
                iconBg={T.purplePale}
                iconColor={T.purple}
                label="Certified"
                value={fmt(certified)}
                sub={`${pct(certified, certBase)} vs expected`}
                subColor={T.purple}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={12 / 5}>
            {summaryLoading ? (
              <Skeleton variant="rounded" height={96} />
            ) : (
              <KpiCard
                icon={<PersonOffOutlinedIcon />}
                iconBg={T.redPale}
                iconColor={T.red}
                label="Not Attended"
                value={fmt(notAttended)}
                sub={`${pct(notAttended, base)} vs expected`}
                subColor={T.red}
              />
            )}
          </Grid>
        </Grid>

        {/* ── Charts row 1 ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* 1. Summary by District */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  1. Summary by District
                </Typography>
                <Box sx={{ height: 260, position: "relative" }}>
                  {summaryLoading ? (
                    <Skeleton variant="rounded" height="100%" />
                  ) : (summary.byDistrict || []).length ? (
                    <Bar data={districtBarData} options={districtBarOpts} />
                  ) : (
                    <EmptyChart message="No district data for this filter." />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 2. Attendance Status */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  2. Attendance Status
                </Typography>
                <Box sx={{ height: 260, position: "relative" }}>
                  {summaryLoading ? (
                    <Skeleton variant="rounded" height="100%" />
                  ) : present + absent + excused > 0 ? (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "36%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: T.primary }}>
                          {fmt(uniqueTeachers)}
                        </Typography>
                        <Typography sx={{ fontSize: "0.625rem", color: T.muted }}>Total Teachers</Typography>
                      </Box>
                      <Doughnut data={attendanceDonutData} options={donutOptions} />
                    </>
                  ) : (
                    <EmptyChart message="No attendance data for this filter." />
                  )}
                </Box>
                {!summaryLoading && (
                  <Box sx={{ mt: 1 }}>
                    {[
                      { label: "Attended", val: present, color: T.chartBlue },
                      { label: "Absent", val: absent, color: T.chartGreen },
                      { label: "Excused", val: excused, color: T.chartOrange },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                          <Typography sx={{ fontSize: "0.75rem", color: T.primary }}>{item.label}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.75rem", color: T.muted }}>
                          {fmt(item.val)} ({pct(item.val, present + absent + excused)})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* 3. Certification Summary */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  3. Certification Summary
                </Typography>
                <Box sx={{ height: 260, position: "relative" }}>
                  {summaryLoading ? (
                    <Skeleton variant="rounded" height="100%" />
                  ) : uniqueTeachers > 0 ? (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "36%",
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                          pointerEvents: "none",
                          zIndex: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: T.primary }}>
                          {fmt(uniqueTeachers)}
                        </Typography>
                        <Typography sx={{ fontSize: "0.625rem", color: T.muted }}>Total Teachers</Typography>
                      </Box>
                      <Doughnut data={certificationDonutData} options={donutOptions} />
                    </>
                  ) : (
                    <EmptyChart message="No certification data for this filter." />
                  )}
                </Box>
                {!summaryLoading && (
                  <Box sx={{ mt: 1 }}>
                    {[
                      { label: "Certified", val: certified, color: T.chartBlue },
                      { label: "Not Certified", val: Math.max(0, uniqueTeachers - certified), color: T.chartGreen },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                          <Typography sx={{ fontSize: "0.75rem", color: T.primary }}>{item.label}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.75rem", color: T.muted }}>
                          {fmt(item.val)} ({pct(item.val, uniqueTeachers)})
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Charts row 2 ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* 4. Top Trainings */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  4. Top Trainings
                </Typography>
                <Box sx={{ height: 260, position: "relative" }}>
                  {summaryLoading ? (
                    <Skeleton variant="rounded" height="100%" />
                  ) : (summary.topTrainings || []).length ? (
                    <Bar data={topTrainingsBarData} options={topTrainingsBarOpts} />
                  ) : (
                    <EmptyChart message="No training data for this filter." />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 5. Position */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  5. Position
                </Typography>
                <Box sx={{ height: 260, position: "relative" }}>
                  {summaryLoading ? (
                    <Skeleton variant="rounded" height="100%" />
                  ) : (summary.byPosition || []).length ? (
                    <Bar data={positionBarData} options={positionBarOpts} />
                  ) : (
                    <EmptyChart message="No position data for this filter." />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 6. Performance Table */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShell, height: "100%" }}>
              <CardContent sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: T.primary, mb: 1.5 }}>
                  6. Performance Summary
                </Typography>
                {summaryLoading ? (
                  <Skeleton variant="rounded" height={200} />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ "& th": { fontSize: "0.6875rem", fontWeight: 700, color: T.primary, bgcolor: "#f8fafc", py: 1.25 } }}>
                          <TableCell align="right">Expected</TableCell>
                          <TableCell align="right">Attended</TableCell>
                          <TableCell align="right">Certified</TableCell>
                          <TableCell align="right">Not Attended</TableCell>
                          <TableCell align="right">Attend. Rate</TableCell>
                          <TableCell align="right">Cert. Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{ "& td": { fontSize: "0.75rem", py: 1.25 } }}>
                          <TableCell align="right">{fmt(expectedRecords)}</TableCell>
                          <TableCell align="right">{fmt(present)}</TableCell>
                          <TableCell align="right">{fmt(certified)}</TableCell>
                          <TableCell align="right">{fmt(notAttended)}</TableCell>
                          <TableCell align="right" sx={{ color: T.green, fontWeight: 700 }}>
                            {pct(present, base)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: T.purple, fontWeight: 700 }}>
                            {pct(certified, certBase)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Footer note ──────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: T.muted }} />
          <Typography sx={{ fontSize: "0.75rem", color: T.muted }}>
            Data reflects the selected filters and reporting period.
          </Typography>
        </Box>

        {/* ── Attendance list table (preserved) ────────────────────────────── */}
        <Card elevation={0} sx={{ ...cardShell, overflow: "hidden" }}>
          <CardContent sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 2, pb: 1.5, flexWrap: "wrap", gap: 1 }}>
              <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: T.primary }}>
                Attendance Records
              </Typography>
              <Typography variant="caption" sx={{ color: T.muted }}>
                {loading ? "Loading…" : `${totalRecords.toLocaleString()} records · Page ${page} of ${totalPages}`}
              </Typography>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={42} sx={{ color: "#078ece" }} />
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "#fafafa",
                        "& th": {
                          borderBottom: T.cardBorder,
                          color: T.primary,
                          fontWeight: 700,
                          fontSize: "0.8125rem",
                          py: 2,
                        },
                      }}
                    >
                      <TableCell>Training</TableCell>
                      <TableCell>Trainee</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Date of birth</TableCell>
                      <TableCell>Qualification</TableCell>
                      <TableCell>Session date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!rows.length ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography color="textSecondary" sx={{ py: 3 }}>
                            No attendance rows match the selected filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((r) => (
                        <TableRow
                          hover
                          key={`${r.attendanceId}_${r.trainingId}_${r.traineeId}`}
                          sx={{ "& td": { borderColor: "#f1f5f9", fontSize: "0.875rem" } }}
                        >
                          <TableCell sx={{ py: 2.25, verticalAlign: "top", maxWidth: 280 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: T.primary, lineHeight: 1.45, mb: 0.5 }}>
                              {r.trainingTitle || "—"}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: T.muted, display: "block", mb: 0.35 }}>
                              ({r.trainingId || "—"})
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: T.muted, lineHeight: 1.45 }}>
                              {[r.programName, r.subProgramName].filter(Boolean).join(" → ")}
                              {r.providerName ? ` · ${r.providerName}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2.25, verticalAlign: "top", minWidth: 140 }}>
                            <Typography sx={{ fontSize: "0.875rem", color: T.primary }}>{r.traineeName || "—"}</Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: T.muted, mt: 0.35, display: "block" }}>
                              ID {r.employeeId || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2.25, whiteSpace: "nowrap", color: T.primary, fontSize: "0.875rem" }}>
                            {formatDisplay(r.gender)}
                          </TableCell>
                          <TableCell sx={{ py: 2.25, whiteSpace: "nowrap", color: T.primary, fontSize: "0.875rem" }}>
                            {formatDateOfBirth(r.dateOfBirth)}
                          </TableCell>
                          <TableCell sx={{ py: 2.25, verticalAlign: "top", maxWidth: 200, color: T.primary, fontSize: "0.875rem" }}>
                            {formatDisplay(r.qualification)}
                            {r.specialization ? (
                              <Typography component="span" sx={{ fontSize: "0.75rem", color: T.muted, display: "block", mt: 0.35 }}>
                                {r.specialization}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ py: 2.25, verticalAlign: "top" }}>
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                              <EventIcon fontSize="small" sx={{ mr: 0.75, mt: "2px", color: T.muted }} />
                              <Box>
                                <Typography sx={{ fontSize: "0.875rem", color: T.primary }}>
                                  {r.attendanceDate ? moment(r.attendanceDate).format("MMM D, YYYY") : "—"}
                                </Typography>
                                <Typography sx={{ fontSize: "0.75rem", color: T.muted, mt: 0.35, display: "block" }}>
                                  Cohort{formatRange(r.cohortStartDate, r.cohortEndDate)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {totalRecords > pageSize && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
            <ReactPaginate
              nextLabel={<span aria-hidden>›</span>}
              onPageChange={({ selected }) => setPage(selected + 1)}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              forcePage={page - 1}
              previousLabel={<span aria-hidden>‹</span>}
              containerClassName="pagination"
              pageLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
              previousLinkClassName="btn btn-outline-dark btn-sm mr-1 ml-1"
              nextLinkClassName="btn btn-outline-dark btn-sm ml-1"
              activeLinkClassName="active"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

function formatDisplay(value) {
  const s = String(value ?? "").trim();
  return s || "—";
}

function formatDateOfBirth(value) {
  if (value == null || value === "") return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MMM D, YYYY") : "—";
}

function formatMark(mark) {
  const m = String(mark || "").toUpperCase();
  if (m === "ATTENDED") return "Present";
  if (m === "ABSENT") return "Absent";
  return m || "—";
}

function formatRemark(remark, mark) {
  const r = String(remark || "").trim();
  if (!r) return "";
  if (String(mark).toUpperCase() === "ATTENDED" && /^attended$/i.test(r)) return "";
  return r;
}

function formatRange(start, end) {
  const a = start ? moment(start).format("MMM D") : "";
  const b = end ? moment(end).format("MMM D, YYYY") : "";
  if (!a && !b) return "";
  if (a && b) return ` ${a} – ${b}`;
  return ` ${a || b}`;
}

const mapStateToProps = ({ user }) => ({ user });
export default connect(mapStateToProps)(TrainingAttendanceReport);
